import { m } from "@plannello/i18n/messages";
import { cx } from "@/lib/cx";

const sizes = {
  sm: "size-5 rounded-mark text-[11px]",
  md: "size-6 rounded-mark text-xs",
  lg: "size-8 rounded-control text-sm",
  xl: "size-10 rounded-control text-base",
} as const;

/**
 * First letter of the name, uppercased; grapheme-safe for umlauts and emoji. An unnamed instance
 * falls back to the product name, so the default mark is the Plannello "P".
 */
export function monogram(name: string) {
  const trimmed = name.trim() || m.app_name();
  const first = [...trimmed][0] ?? "P";
  return first.toLocaleUpperCase();
}

/**
 * The instance monogram: a monochrome tile (graphite in light, near-white in dark) with a 1px top
 * sheen. It is the brand mark of this tracker instance, so it never uses the accent colour.
 * Decorative: the instance name is always rendered next to it or in an aria-label nearby.
 */
export function InstanceMark({
  name,
  size = "sm",
  className,
}: {
  name: string;
  size?: keyof typeof sizes;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        "inline-flex shrink-0 select-none items-center justify-center bg-mark font-semibold leading-none text-mark-fg",
        "shadow-[inset_0_1px_0_var(--highlight),0_1px_2px_var(--shadow-soft),0_0_0_1px_var(--shadow-edge)]",
        sizes[size],
        className,
      )}
    >
      {monogram(name)}
    </span>
  );
}

/**
 * Product wordmark: the default mark (the product monogram) plus "Plannello" set in Geist 600 with
 * tight tracking. Used on pre-auth screens (login, setup), where the product rather than an instance
 * is speaking. The tile is the same shape as every instance mark, so a fresh instance and the
 * product read as one family.
 */
export function Wordmark({ size = "md", className }: { size?: "md" | "lg"; className?: string }) {
  const name = m.app_name();
  return (
    <span className={cx("inline-flex items-center gap-2", className)}>
      <InstanceMark name={name} size={size} />
      <span className={cx("font-semibold tracking-tight text-fg", size === "lg" ? "text-lg" : "text-base")}>
        {name}
      </span>
    </span>
  );
}
